import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { downloadAgreementPdf } from "../lib/downloadPdf";
import { usePublicSettings } from "../hooks/usePublicSettings";
import VehicleSnapshot from "../components/booking/VehicleSnapshot";
import TermsAgreement from "../components/booking/TermsAgreement";
import PriceBreakdown from "../components/booking/PriceBreakdown";
import { ShieldCheck, FileDown } from "lucide-react";

export default function Booking() {
  const { id } = useParams();
  const settings = usePublicSettings();
  const [b, setB] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/bookings/${id}`)
      .then((r) => setB(r.data))
      .catch((e) => setError(formatApiError(e.response?.data?.detail) || "Not found"));
  }, [id]);

  if (error) return <div className="p-12 text-center text-neutral-500">{error}</div>;
  if (!b) return <div className="p-12 text-center text-neutral-500">Loading…</div>;

  const handlePay = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/payments/checkout", {
        booking_id: b.id,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
      setLoading(false);
    }
  };

  return (
    <div className="px-6 md:px-12 lg:px-20 py-12" data-testid="booking-review-page">
      <p className="overline mb-3">Step 2 of 3</p>
      <h1 className="font-display text-4xl md:text-5xl tracking-tight">
        Review & <em className="italic">sign</em>.
      </h1>
      <p className="mt-3 text-neutral-600 max-w-xl">
        Please review your reservation details and the rental agreement below. Payment is processed securely via Stripe.
      </p>

      <div className="grid lg:grid-cols-12 gap-10 mt-12">
        {/* Left: details + agreement */}
        <div className="lg:col-span-7">
          <VehicleSnapshot b={b} />
          <TermsAgreement
            b={b}
            settings={settings}
            agreed={agreed}
            onAgreedChange={setAgreed}
          />
        </div>

        {/* Right: price + pay */}
        <div className="lg:col-span-5">
          <div className="bg-white border border-black/10 p-6 md:p-8 sticky top-28">
            <p className="overline mb-4">Price Summary</p>
            <PriceBreakdown b={b} />

            <button
              onClick={handlePay}
              disabled={!agreed || loading || b.payment_status === "paid" || b.payment_status === "partial"}
              className="mt-8 w-full bg-black text-white py-4 hover:bg-neutral-800 transition-colors text-sm tracking-wide disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="pay-button"
            >
              {payButtonLabel(b, loading)}
            </button>

            <div className="mt-6 flex items-center gap-2 text-xs text-neutral-500">
              <ShieldCheck size={14} />
              <span>Encrypted payment via Stripe. We never store card details.</span>
            </div>

            <button
              type="button"
              onClick={() => downloadAgreementPdf(b.id)}
              className="mt-4 w-full inline-flex items-center justify-center gap-2 border border-black/15 hover:border-black px-4 py-3 transition-colors text-sm"
              data-testid="download-agreement-button"
            >
              <FileDown size={14} /> Download Rental Agreement (PDF)
            </button>

            {error && <p className="text-red-600 text-sm mt-4" data-testid="payment-error">{error}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

function payButtonLabel(b, loading) {
  if (b.payment_status === "paid") return "Already Paid";
  if (b.payment_status === "partial") return `Deposit paid · €${Number(b.paid_amount || b.advance_deposit || 0).toFixed(2)}`;
  if (loading) return "Redirecting…";
  const advance = Number(b.advance_deposit || 0);
  const total = Number(b.total || 0);
  const isPartial = advance > 0 && advance < total;
  if (isPartial) {
    return `Pay €${advance.toFixed(2)} deposit with Stripe`;
  }
  return `Pay €${total.toFixed(2)} with Stripe`;
}
